"use client";

import {
  getWebSettingFiledData,
  websiteSettingsUpdate,
} from "@/actions/apiClientActions/website-settings";
import { globalStyleObj } from "@/app/assets/styles";
import {
  LanguageTabs,
  NormalSwitchInputField,
  NormalTextInputField,
  SettingsUpdateButton,
  TranslationNormalForm,
} from "@/components";
import { useErrorToast, useSuccessToast } from "@/lib/hooks";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { isValidJSONData } from "@/utils/website-settings-helper";
import { useCallback, useEffect, useMemo, useState } from "react";

const HeaderSettings = ({ userId, languagesResponse }) => {
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );

  const [formData, setFormData] = useState({
    show_language_switcher: "0",
    show_socials_switcher: "0",
    enable_sticky_header: "0",
    helpline_number: "",
    helpline_email: "",
    page_label_one: "",
    page_label_two: "",
    page_label_three: "",
    button_label: "",
    button_label_two: "",
    button_label_three: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [activeTab, setActiveTab] = useState("en");

  const activeLanguages = useMemo(
    () => languagesResponse?.fetchData?.filter((lang) => lang.status) || [],
    [languagesResponse]
  );

  // Fetch data
  const getAllHeaderSettingFieldsData = useCallback(async () => {
    setIsFetching(true);

    try {
      const response = await getWebSettingFiledData(
        userId,
        Object.keys(formData)
      );

      if (response.targetFieldsData) {
        Object.entries(response.targetFieldsData).forEach(([key, value]) => {
          setFormData((prev) => ({
            ...prev,
            [key]: isValidJSONData(value) ? JSON.parse(value) : value,
          }));
        });
      }
    } catch (error) {
      console.error("Failed to fetch header settings data:", error);
      useErrorToast("Failed to fetch header settings.");
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!isSubmitting) {
      getAllHeaderSettingFieldsData();
    }
  }, [isSubmitting]);

  // Handle Text Input Field State
  const onChangeTextInputField = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Language Switch State
  const onChangeLanguageSwtichState = () => {
    setFormData((prev) => ({
      ...prev,
      show_language_switcher: prev.show_language_switcher === "0" ? "1" : "0",
    }));
  };

  // Handle Socials Switch State
  const onChangeSocialsSwtichState = () => {
    setFormData((prev) => ({
      ...prev,
      show_socials_switcher: prev.show_socials_switcher === "0" ? "1" : "0",
    }));
  };

  // Handle Sticky Header Switch State
  const onChangeHeaderSwtichState = () => {
    setFormData((prev) => ({
      ...prev,
      enable_sticky_header: prev.enable_sticky_header === "0" ? "1" : "0",
    }));
  };

  // Handle OnSubmit Form
  const handleHeaderSettingsDataSubmit = async (e) => {
    e.preventDefault();

    const formatedFormData = {
      types: Object.keys(formData),
      ...formData,
    };

    setIsSubmitting(true);

    try {
      const response = await websiteSettingsUpdate(userId, formatedFormData);
      if (response.success) {
        useSuccessToast(response?.message || "Settings updated successfully.");
      } else {
        useErrorToast(response?.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Failed to update header settings:", error);
      useErrorToast("Failed to update header settings.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} w-full max-w-[800px] mx-auto mt-[40px] rounded-sm shadow-light overflow-hidden`}
    >
      <h1 className="py-3 px-4 md:px-5 text-[16px] font-poppins-md text-dark-weight-550 dark:text-light-weight-550 border-b border-[#000]/20 dark:border-[#fff]/10">
        Header Settings
      </h1>

      {activeLanguages.length > 1 && (
        <LanguageTabs
          languages={activeLanguages}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      {activeTab === "en" ? (
        <form onSubmit={handleHeaderSettingsDataSubmit} className="py-4 px-5">
          {/* Show Language Switcher */}
          <NormalSwitchInputField
            isFetching={isFetching}
            labelText="Show Language Switcher?"
            fieldId="show-language-switcher"
            fieldName="show_language_switcher"
            switchStatus={formData?.show_language_switcher || "0"}
            onChangeSwtichState={onChangeLanguageSwtichState}
            customBodySize="h-5 w-10"
            customThumbSize="h-4 w-5"
          />

          {/* Show Socials Switcher */}
          <NormalSwitchInputField
            isFetching={isFetching}
            labelText="Show Socials Switcher?"
            fieldId="show-socials-switcher"
            fieldName="show_socials_switcher"
            switchStatus={formData?.show_socials_switcher || "0"}
            onChangeSwtichState={onChangeSocialsSwtichState}
            extraContainerClasses="mt-5"
            customBodySize="h-5 w-10"
            customThumbSize="h-4 w-5"
          />

          {/* Enable Sticky Header */}
          <NormalSwitchInputField
            isFetching={isFetching}
            labelText="Enable Stikcy Header?"
            fieldId="enable-sticky-header"
            fieldName="enable_sticky_header"
            switchStatus={formData?.enable_sticky_header || "0"}
            onChangeSwtichState={onChangeHeaderSwtichState}
            extraContainerClasses="mt-5"
            customBodySize="h-5 w-10"
            customThumbSize="h-4 w-5"
          />

          {/* Helpline Number */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Helpline Number"
            fieldId="helpline-number"
            fieldName="helpline_number"
            placeholderText="Helpline Number"
            inputValue={formData?.helpline_number || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
          />

          {/* Helpline Email */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Helpline Email"
            fieldId="helpline-email"
            fieldName="helpline_email"
            placeholderText="Helpline Email"
            inputValue={formData?.helpline_email || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
          />

          {/* Page Label 1 */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Page Label 1"
            fieldId="page-label-one"
            fieldName="page_label_one"
            placeholderText="Page Label 1"
            inputValue={formData?.page_label_one || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          {/* Page Label 2 */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Page Label 2"
            fieldId="page-label-two"
            fieldName="page_label_two"
            placeholderText="Page Label 2"
            inputValue={formData?.page_label_two || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          {/* Page Label 3 */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Page Label 3"
            fieldId="page-label-three"
            fieldName="page_label_three"
            placeholderText="Page Label 3"
            inputValue={formData?.page_label_three || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          {/* Top Header Button label */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Top Header Button Label"
            fieldId="button-label-1"
            fieldName="button_label"
            placeholderText="Top Header Button Label"
            inputValue={formData?.button_label || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          {/* Bottom Header Button Label 1 */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Bottom Header Button Label 1"
            fieldId="button-label-2"
            fieldName="button_label_two"
            placeholderText="Bottom Header Button Label 1"
            inputValue={formData?.button_label_two || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          {/* Bottom Header Button Label 2 */}
          <NormalTextInputField
            isFetching={isFetching}
            labelText="Bottom Header Button Label 2"
            fieldId="button-label-3"
            fieldName="button_label_three"
            placeholderText="Bottom Header Button Label 2"
            inputValue={formData?.button_label_three || ""}
            onChangeTextInputField={onChangeTextInputField}
            extraContainerClasses="mt-5"
            translateField={true}
          />

          <div className="flex justify-end mt-5">
            <SettingsUpdateButton
              isFetching={isFetching}
              isSubmitting={isSubmitting}
              colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
              btnText="Update"
            />
          </div>
        </form>
      ) : (
        <TranslationNormalForm
          fields={[
            {
              labelText: "Page Label 1",
              fieldId: `page-label-one-lang-${activeTab}`,
              fieldName: "page_label_one",
              placeholderText: "Page Label 1",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
            },
            {
              labelText: "Page Label 2",
              fieldId: `page-label-two-lang-${activeTab}`,
              fieldName: "page_label_two",
              placeholderText: "Page Label 2",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
              extraContainerClasses: "mt-5",
            },
            {
              labelText: "Page Label 3",
              fieldId: `page-label-three-lang-${activeTab}`,
              fieldName: "page_label_three",
              placeholderText: "Page Label 3",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
              extraContainerClasses: "mt-5",
            },
            {
              labelText: "Top Header Button Label",
              fieldId: `button-label-1-lang-${activeTab}`,
              fieldName: "button_label",
              placeholderText: "Top Header Button Label",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
              extraContainerClasses: "mt-5",
            },
            {
              labelText: "Bottom Header Button Label 1",
              fieldId: `button-label-2-lang-${activeTab}`,
              fieldName: "button_label_two",
              placeholderText: "Bottom Header Button Label 1",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
              extraContainerClasses: "mt-5",
            },
            {
              labelText: "Bottom Header Button Label 2",
              fieldId: `button-label-3-lang-${activeTab}`,
              fieldName: "button_label_three",
              placeholderText: "Bottom Header Button Label 2",
              inputValue: "",
              isTextEditorType: false,
              translateField: true,
              extraContainerClasses: "mt-5",
            },
          ]}
          userId={userId}
          activeTab={activeTab}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}
    </div>
  );
};

export default HeaderSettings;
